import { Link } from 'react-router-dom';
import { Lightbulb, Users, TrendingUp, Shapes, BarChart3, ArrowRight } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Reveal, SectionHeading, Eyebrow } from '../components/Reveal';
import { Counter } from '../components/Counter';
import { FAQS } from '../lib/site';
import { useSEO } from '../lib/seo';
import { CtaBanner } from '../sections/CtaBanner';

const STATS = [
  {
    n: 120,
    suffix: '+',
    label: 'Happy Client',
    copy: 'Businesses and individuals who trust our solutions and grow with us.',
    avatars: false,
  },
  {
    n: 500,
    suffix: '+',
    label: 'Projects Completed',
    copy: 'Successfully delivered solutions built with precision and reliability.',
    dark: true,
  },
  {
    n: 95,
    suffix: '%',
    label: 'Customer Satisfaction',
    copy: 'Our commitment to quality ensures every client experience exceeds expectations.',
    avatars: true,
  },
];

const VALUES = [
  {
    icon: <Lightbulb className="h-5 w-5" />,
    title: 'Innovating for Tomorrow',
    copy: 'Collaborating with our clients to create smarter solutions for tomorrow.',
  },
  {
    icon: <Users className="h-5 w-5" />,
    title: 'Empowering Collaboration',
    copy: 'Creating the next generation of solutions through collaboration and vision.',
  },
  {
    icon: <TrendingUp className="h-5 w-5" />,
    title: 'Driving Sustainable Growth',
    copy: 'Building solutions that support long-term success and responsible innovation.',
  },
  {
    icon: <Shapes className="h-5 w-5" />,
    title: 'Shaping Smarter Solutions',
    copy: "Where ideas, technology, and teamwork come together to shape what's next.",
  },
];

export const About = () => {
  useSEO({
    title: 'About Us — CapableBee',
    description:
      'From Idea to Impact — transforming vision into technology that delivers real-world results. Learn how CapableBee builds AI voice solutions for growing businesses.',
  });

  return (
    <main className="pt-[72px]" data-testid="about-page">
      <header className="relative overflow-hidden bg-gradient-to-b from-brand-ice via-brand-iceSoft to-white py-16 lg:py-20">
        <div className="bg-grid mask-fade-b pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="container-x relative mx-auto max-w-3xl text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-lineBlue bg-white/80 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-brand-skyDeep shadow-card">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-honey" aria-hidden="true" />
              About Us
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-brand-navy sm:text-5xl lg:text-6xl">
              From Idea to{' '}
              <span className="bg-gradient-to-r from-brand-sky to-brand-skyDeep bg-clip-text text-transparent">
                Impact
              </span>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-brand-body/90 sm:text-lg">
              Transforming vision into technology that delivers real-world results.
            </p>
          </Reveal>
        </div>
      </header>

      <section className="py-16 lg:py-20" data-testid="about-stats">
        <div className="container-x grid gap-5 md:grid-cols-3">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className="h-full" data-testid={`about-stat-${i}`}>
              <div
                className={`flex h-full flex-col rounded-3xl border p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lift ${
                  s.dark
                    ? 'border-brand-panel bg-gradient-to-b from-brand-navy to-brand-panel text-white shadow-lift'
                    : 'border-brand-line bg-white shadow-card'
                }`}
              >
                <div className="flex items-center gap-3">
                  {s.dark ? (
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-brand-honey">
                      <BarChart3 className="h-5 w-5" />
                    </span>
                  ) : s.avatars ? (
                    <span className="flex items-center">
                      {['/brand/avatar-rohan.jpg', '/brand/avatar-david.jpg', '/brand/avatar-sophia.jpg'].map((src, j) => (
                        <img
                          key={src}
                          src={src}
                          alt=""
                          aria-hidden="true"
                          width="36"
                          height="36"
                          loading="lazy"
                          className={`h-9 w-9 rounded-full border-2 border-white object-cover shadow-card ${j > 0 ? '-ml-2.5' : ''}`}
                        />
                      ))}
                    </span>
                  ) : (
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-ice">
                      <img src="/brand/icon.png" alt="" aria-hidden="true" className="h-7 w-7" />
                    </span>
                  )}
                </div>
                <p className={`mt-5 text-sm leading-relaxed ${s.dark ? 'text-white/75' : 'text-slate-600'}`}>
                  {s.copy}
                </p>
                <p
                  className={`mt-auto pt-7 text-5xl font-extrabold tracking-tight ${
                    s.dark ? 'text-white' : 'text-brand-navy'
                  }`}
                >
                  <Counter to={s.n} suffix={s.suffix} />
                </p>
                <p className={`mt-1.5 text-sm font-bold ${s.dark ? 'text-white/70' : 'text-brand-body/70'}`}>
                  {s.label}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-brand-iceSoft py-20 lg:py-24" data-testid="about-values">
        <div className="container-x">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div className="max-w-xl">
              <Reveal>
                <Eyebrow>About Us</Eyebrow>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="mt-4 text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl lg:text-4xl">
                  Building the Future Together
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.12}>
              <p className="max-w-sm text-sm leading-relaxed text-slate-600 lg:text-right">
                Working together to turn ideas into technology that shapes the future.
              </p>
            </Reveal>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.07} data-testid={`about-value-${i}`}>
                <div className="group h-full rounded-3xl border border-brand-line bg-white p-8 shadow-card transition-all duration-300 hover:-translate-y-1 hover:shadow-lift">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-ice text-brand-skyDeep transition-colors group-hover:bg-brand-sky group-hover:text-white">
                    {v.icon}
                  </span>
                  <h3 className="mt-5 text-lg font-bold tracking-tight text-brand-navy">{v.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{v.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24" data-testid="about-faq">
        <div className="container-x">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div className="max-w-xl">
              <Reveal>
                <Eyebrow>FAQ</Eyebrow>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="mt-4 text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl lg:text-4xl">
                  Everything You Need to Know
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.12}>
              <div className="max-w-sm lg:text-right">
                <p className="text-sm leading-relaxed text-slate-600">
                  Browse answers and guides to common questions, and if something's missing, our team is here to help.
                </p>
                <Link
                  to="/contact"
                  data-testid="about-get-in-touch"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-navy px-5 py-2.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:shadow-lift"
                >
                  Get in Touch
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mx-auto mt-10 max-w-3xl space-y-4" data-testid="about-faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`about-faq-${i}`}
                  className="rounded-2xl border border-brand-line bg-white px-6 shadow-card"
                  data-testid={`about-faq-item-${i}`}
                >
                  <AccordionTrigger className="py-5 text-left text-base font-bold text-brand-navy hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-sm leading-relaxed text-slate-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <CtaBanner />
    </main>
  );
};
